import React, { useState } from "react";
import { Form, Table, Spinner, Alert, Button, Tabs, Tab, Modal } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { useSelector, useDispatch } from "react-redux";
import { selectCustomRoles, addCustomRole, updateCustomRole, deleteCustomRole } from "../../redux/slices/customRolesSlice";

function UserManage() {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const customRoles = useSelector(selectCustomRoles);
  
  // AD Group states
  const [selectedGroup, setSelectedGroup] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Custom role states
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: "", message: "" });

  // Fetch members based on group ID
  function fetchMembers(groupId) {
    if (!groupId || accounts.length === 0) return;

    setLoading(true);
    setError(null); // Reset any previous errors
    setMembers([]); // Reset members list

    const account = accounts[0]; // Use the first logged-in account

    instance
      .acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"],
        account: account, // Pass the active account
      })
      .then((response) => {
        return fetch(
          `https://graph.microsoft.com/v1.0/groups/${groupId}/members`,
          {
            headers: {
              Authorization: `Bearer ${response.accessToken}`,
            },
          }
        );
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch group members");
        }
        return response.json();
      })
      .then((data) => {
        setMembers(data.value); // Set members from API response
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching group members:", err);
        setError(err.message);
        setLoading(false);
      });
  }

  // Handle group selection change
  function handleGroupChange(e) {
    const groupId = e.target.value;
    setSelectedGroup(groupId);
    fetchMembers(groupId);
  }
  
  // Custom role handlers
  const handleSubmitRole = (e) => {
    e.preventDefault();
    if (!email || !role) {
      setStatusMessage({ type: "danger", message: "Email and Role are required" });
      return;
    }

    if (editMode) {
      // Update existing role
      dispatch(updateCustomRole({ email, role }));
      setStatusMessage({ type: "success", message: `Updated role for ${email}` });
    } else {
      // Add new role
      if (customRoles[email]) {
        setStatusMessage({ type: "warning", message: "This email already has a custom role. Edit it instead." });
        return;
      }
      dispatch(addCustomRole({ email, role }));
      setStatusMessage({ type: "success", message: `Added ${role} role for ${email}` });
    }

    // Reset form
    setEmail("");
    setRole("");
    setEditMode(false);
    setShowModal(false);
  };

  const handleEditRole = (email, role) => {
    setEmail(email);
    setRole(role);
    setEditMode(true);
    setShowModal(true);
  };

  const handleDeleteRole = (email) => {
    dispatch(deleteCustomRole(email));
    setStatusMessage({ type: "success", message: `Removed custom role for ${email}` });
  };

  const handleOpenModal = () => {
    setEmail("");
    setRole("");
    setEditMode(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEmail("");
    setRole("");
    setEditMode(false);
    setShowModal(false);
  };

  return (
    <div>
      <h2>User Management</h2>
      
      <Tabs defaultActiveKey="adGroups" className="mb-3">
        <Tab eventKey="adGroups" title="Azure AD Groups">
          {/* Dropdown for selecting a group */}
          <Form.Group controlId="groupSelect" className="mb-3">
            <Form.Label>Select Group</Form.Label>
            <Form.Control
              as="select"
              value={selectedGroup}
              onChange={handleGroupChange}
            >
              <option value="">Select a group...</option>
              <option value={process.env.REACT_APP_GROUP_ADMIN}>
                Admin Group 
              </option>
              <option value={process.env.REACT_APP_GROUP_APPS}>
                Apps Group
              </option>
              <option value={process.env.REACT_APP_GROUP_ENGINEER}>
                Engineer Group
              </option>
              <option value={process.env.REACT_APP_GROUP_TESTPIT}>
                Test Pit Group
              </option>
              <option value={process.env.REACT_APP_GROUP_PSN}>
                PSN Group
              </option>
              <option value={process.env.REACT_APP_GROUP_SALES}>
                Sales Group
              </option>
              <option value={process.env.REACT_APP_GROUP_MECH}>
                Mechanical Engineers Group
              </option>
              <option value={process.env.REACT_APP_GROUP_ELEC}>
                Electrical Engineers Group
              </option>
              <option value={process.env.REACT_APP_GROUP_CONTROL}>
                Controls Group
              </option>
              <option value={process.env.REACT_APP_GROUP_SCHEDULING}>
                Scheduling Group
              </option>
              <option value={process.env.REACT_APP_GROUP_ENGINEERADMIN}>
                Engineer Admin Group
              </option>
              <option value={process.env.REACT_APP_GROUP_ACCOUNTING}>
                Accounting Group
              </option>
              <option value={process.env.REACT_APP_GROUP_NP}>
                North Prairie Group
              </option>
            </Form.Control>
          </Form.Group>

          {/* Display loading spinner if fetching members */}
          {loading && <Spinner animation="border" />}

          {/* Display error if occurred */}
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Display members table */}
          {!loading && members.length > 0 && (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td>{member.displayName || "N/A"}</td>
                    <td>{member.mail || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>
        
        <Tab eventKey="customRoles" title="Custom Role Assignments">
          {statusMessage.message && (
            <Alert variant={statusMessage.type} onClose={() => setStatusMessage({ type: "", message: "" })} dismissible>
              {statusMessage.message}
            </Alert>
          )}
          
          <div className="mb-3">
            <Button variant="primary" onClick={handleOpenModal}>
              Assign New Custom Role
            </Button>
          </div>
          
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(customRoles).map(([email, role]) => (
                <tr key={email}>
                  <td>{email}</td>
                  <td>{role}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEditRole(email, role)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteRole(email)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {Object.keys(customRoles).length === 0 && (
                <tr>
                  <td colSpan="3" className="text-center">
                    No custom roles defined
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Tab>
      </Tabs>
      
      {/* Modal for adding/editing custom roles */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit Custom Role" : "Add Custom Role"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitRole}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                required
                disabled={editMode}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select value={role} onChange={(e) => setRole(e.target.value)} required>
                <option value="">Select a role...</option>
                <option value="Admin">Admin</option>
                <option value="MechanicalEngineer">MechanicalEngineer</option>
                <option value="ElectricalEngineer">ElectricalEngineer</option>
                <option value="ControlEngineer">ControlEngineer</option>
                <option value="Apps">Apps</option>
                <option value="AppsManager">AppsManager</option>
                <option value="PSN">PSN</option>
                <option value="Sales">Sales</option>
                <option value="ReadOnly">ReadOnly</option>
                <option value="NorthPrairie">NorthPrairie</option>
              </Form.Select>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editMode ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default UserManage;
